import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useEffect, useMemo, useRef } from 'react';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import sanitizeHtml from 'sanitize-html';
import { useTheme } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from "../../../lib/Web5Context";
import { getNumberAsLocaleNumber, formatLargeNumber } from "../../../utils/numbers";
import { getVar } from "../../../utils/colors";
import { getEntityFromString } from "../../../utils/getEntityFromString";
import { CHART_REGEX, CHART_REGEX_GLOBAL, DETAIL_REGEX, DETAIL_REGEX_GLOBAL, TABLE_REGEX, TABLE_REGEX_GLOBAL, COPILOT_CHAT_STREAM_IDENTIFIERS, CHART_DEFAULTS } from "./constants";
import { removeTags, removeMarkdown, processLiveMarkdown, getRegexMatch, getRegexExec, isColorDark, getDifference } from "./utils";
import { createInfoBox, createTable } from "./dom-utils";

/**
 * Custom React hook for building and processing chat answers with dynamic content.
 * Handles text processing, chart generation, table creation, and interactive elements
 * for a copilot chat interface.
 *
 * @param {Object} params - Hook parameters
 * @param {Object} params.customEventHandlers - Custom event handlers for user interactions
 * @param {Function} [params.customEventHandlers.redirectToDetail] - Handler for detail link clicks
 *
 * @returns {Object} Hook return object
 * @returns {Function} insertTextInDOM - Function to insert text content into DOM with live processing
 * @returns {Function} cleanChatTextForClipboard - Function to clean text for clipboard copying
 * @returns {Function} parseCopilotEmbeds - Function to parse copilot embeds
 *
 * @example
 * ```jsx
 * const { insertTextInDOM, cleanChatTextForClipboard, parseCopilotEmbeds } = useBuildAnswer({
 *   customEventHandlers: {
 *     redirectToDetail: (target) => {
 *       // Handle detail link clicks
 *       const detailId = target.getAttribute('data-fm_detail');
 *       navigate(`/detail/${detailId}`);
 *     }
 *   }
 * });
 * ```
 */
const useBuildAnswer = function (_temp) {
  let {
    customEventHandlers
  } = _temp === void 0 ? {} : _temp;
  const {
    colors
  } = useTheme();
  const {
    t
  } = useWeb5Context();
  const chartImagesRef = useRef({});
  const isFirstRender = useRef(true);

  // Clear chart images on mount
  useEffect(() => {
    if (isFirstRender.current) {
      chartImagesRef.current = {};
      isFirstRender.current = false;
    }
  }, []);

  // Memoized replacement functions
  const replacementFunctions = useMemo(() => ({
    /**
     * Replaces detail identifiers with HTML links or bold text
     * @param {boolean} avoidLink - Whether to avoid creating links and use bold text instead
     * @returns {Function} Replacement function
     */
    replaceForDetail: function (avoidLink) {
      if (avoidLink === void 0) {
        avoidLink = false;
      }
      return text => {
        const info = getRegexMatch(text, DETAIL_REGEX);
        const [entityName, id, name] = info.split(',');
        if (!entityName || !id || !name) return text;
        return avoidLink ? `<b>${name}</b>` : `<a data-fm_detail="${entityName}-${id}">${name}</a>`;
      };
    },
    /**
     * Replaces chart identifiers with chart container divs
     * @param {string} text - Text containing chart identifier
     * @returns {string} HTML with chart container
     */
    replaceForChart: text => {
      const id = getRegexMatch(text, CHART_REGEX);
      return id ? `<div data-fm_report="${id}"></div>` : text;
    },
    /**
     * Replaces table identifiers with table container divs
     * @param {string} text - Text containing table identifier
     * @returns {string} HTML with table container
     */
    replaceForTable: text => {
      const id = getRegexMatch(text, TABLE_REGEX);
      return id ? `<div data-fm_table="${id}"></div>` : text;
    }
  }), []);

  /**
   * Builds and renders a Chart.js chart in the specified container
   * @param {string} id - Unique identifier for the chart
   * @param {HTMLElement} container - DOM container to render the chart
   * @param {Object} data - Chart.js configuration data
   */
  const buildChart = useCallback((id, container, data) => {
    var _Object$keys, _data$options;
    if (!data || !((_Object$keys = Object.keys(data)) !== null && _Object$keys !== void 0 && _Object$keys.length)) return;
    const chartConfig = _objectSpread(_objectSpread({}, data), {}, {
      options: _objectSpread(_objectSpread({}, data.options || {}), {}, {
        devicePixelRatio: CHART_DEFAULTS.devicePixelRatio,
        responsive: true,
        layout: _objectSpread(_objectSpread({}, ((_data$options = data.options) === null || _data$options === void 0 ? void 0 : _data$options.layout) || {}), {}, {
          padding: CHART_DEFAULTS.padding
        }),
        animation: {
          onComplete: () => {
            chartImagesRef.current[id] = newChart.toBase64Image();
          }
        },
        plugins: {
          customCanvasBackgroundColor: {
            color: getVar(colors.actionMinor[50])
          },
          tooltip: {
            enabled: true,
            callbacks: {
              title: tooltipItem => tooltipItem.label,
              label: tooltipItem => {
                var _tooltipItem$dataset$, _tooltipItem$dataset;
                let value = (_tooltipItem$dataset$ = tooltipItem.dataset.data) === null || _tooltipItem$dataset$ === void 0 ? void 0 : _tooltipItem$dataset$[tooltipItem.dataIndex];
                if (isNaN(value)) return '';
                value = getNumberAsLocaleNumber(value.toString());
                const label = (tooltipItem === null || tooltipItem === void 0 ? void 0 : (_tooltipItem$dataset = tooltipItem.dataset) === null || _tooltipItem$dataset === void 0 ? void 0 : _tooltipItem$dataset.label) || '';
                return label ? `${label}: ${value}` : value;
              }
            },
            boxPadding: 4
          },
          datalabels: {
            color: context => {
              var _context$dataset;
              const backgroundColors = (context === null || context === void 0 ? void 0 : (_context$dataset = context.dataset) === null || _context$dataset === void 0 ? void 0 : _context$dataset.backgroundColor) || [];
              const backgroundColor = backgroundColors === null || backgroundColors === void 0 ? void 0 : backgroundColors[context.dataIndex];
              const isChartPie = ['pie', 'doughnut'].includes(data.type);
              if (!(backgroundColor !== null && backgroundColor !== void 0 && backgroundColor.length) || !isChartPie) {
                return getVar(colors.grey[700]);
              }
              const darks = backgroundColors.filter(current => {
                var _current$match;
                const rgbArray = (_current$match = current.match(/\d+/g)) === null || _current$match === void 0 ? void 0 : _current$match.map(val => parseInt(val, 10));
                return rgbArray ? isColorDark(rgbArray) : false;
              });
              return darks.length >= Math.ceil(backgroundColors.length / 2) ? getVar(colors.primary.white) : getVar(colors.grey[700]);
            },
            font: {
              weight: ['pie', 'doughnut'].includes(data.type) ? 'bold' : 'normal'
            },
            anchor: 'center',
            formatter: (value, context) => {
              if (!value) return null;
              const hasMultipleDatasets = data.data.datasets.length > 1;
              return hasMultipleDatasets ? `${context.dataset.label}\n${formatLargeNumber(value)}` : formatLargeNumber(value);
            },
            textAlign: 'center',
            display: 'auto'
          }
        }
      }),
      plugins: [ChartDataLabels, {
        id: 'customCanvasBackgroundColor',
        beforeDraw: (chart, args, options) => {
          const {
            ctx
          } = chart;
          ctx.save();
          ctx.globalCompositeOperation = 'destination-over';
          ctx.fillStyle = options.color || getVar(colors.actionMinor[50]);
          ctx.fillRect(0, 0, chart.width, chart.height);
          ctx.restore();
        }
      }]
    });
    const canvas = document.createElement('canvas');
    canvas.width = CHART_DEFAULTS.width;
    canvas.height = CHART_DEFAULTS.height;
    container.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    const newChart = new Chart(ctx, chartConfig);
  }, [colors]);

  /**
   * Creates an info box container for charts and tables
   * @param {Object} params - Info box parameters
   * @param {HTMLElement} params.container - Parent container
   * @param {boolean} params.isChart - Whether this is for a chart
   * @param {boolean} params.isTable - Whether this is for a table
   * @param {string} params.id - Unique identifier
   * @returns {HTMLElement} Created info box body element
   */
  const buildInfoBox = useCallback(_ref => {
    let {
      container,
      isChart,
      isTable,
      id
    } = _ref;
    return createInfoBox({
      container,
      isChart,
      isTable,
      id,
      t
    });
  }, [t]);

  /**
   * Creates a table element from table data
   * @param {Array} tableData - Array of table row objects
   * @returns {HTMLElement} Created table element
   */
  const createTableElement = useCallback(tableData => {
    return createTable(tableData, replacementFunctions.replaceForDetail);
  }, [replacementFunctions]);

  /**
   * Processes live content to create detail links dynamically
   * @param {HTMLElement} textBox - Text container element
   */
  const processLiveRedirect = useCallback(textBox => {
    if (!textBox) return;
    const htmlText = textBox.innerHTML;
    const {
      match,
      info
    } = getRegexExec(htmlText, DETAIL_REGEX);
    if (!match || !info) return;
    const infoArray = info.split(',');
    if (!infoArray.length) return;
    const [entity, id, name] = infoArray;
    if (!entity || !name || !id) return;

    // Remove identifier from the text
    const newText = htmlText.replace(match, '');
    textBox.innerHTML = newText;
    const link = document.createElement('a');
    link.innerText = name;
    link.setAttribute('data-fm_detail', `${entity}-${id}`);
    link.setAttribute('id', id);
    textBox.appendChild(link);
  }, []);

  /**
   * Processes live content to create charts dynamically
   * @param {HTMLElement} textBox - Text container element
   * @param {Object} data - Chart data object with chart configurations
   */
  const processLiveChart = useCallback((textBox, data) => {
    var _data$id, _Object$keys2;
    if (!textBox) return;
    const htmlText = textBox.innerHTML;
    const {
      match,
      info
    } = getRegexExec(htmlText, CHART_REGEX);
    const id = info;
    if (!match || !id || !(data !== null && data !== void 0 && data[id])) return;
    const reportData = (_data$id = data[id]) === null || _data$id === void 0 ? void 0 : _data$id.content;
    const newText = htmlText.replace(match, replacementFunctions.replaceForChart);
    textBox.innerHTML = newText;
    const container = document.querySelector(`[data-fm_report="${id}"]`);
    if (container && reportData && (_Object$keys2 = Object.keys(reportData)) !== null && _Object$keys2 !== void 0 && _Object$keys2.length) {
      const chartBody = buildInfoBox({
        container,
        isChart: true,
        id
      });
      buildChart(id, chartBody, reportData);
    }
  }, [replacementFunctions, buildInfoBox, buildChart]);

  /**
   * Processes live content to create tables dynamically
   * @param {HTMLElement} textBox - Text container element
   * @param {Object} data - Table data object with table configurations
   */
  const processLiveTable = useCallback((textBox, data) => {
    var _data$id2;
    if (!textBox) return;
    const htmlText = textBox.innerHTML;
    const {
      match,
      info
    } = getRegexExec(htmlText, TABLE_REGEX);
    const id = info;
    if (!match || !id || !(data !== null && data !== void 0 && data[id])) return;
    const tableData = (_data$id2 = data[id]) === null || _data$id2 === void 0 ? void 0 : _data$id2.content;
    const newText = htmlText.replace(match, replacementFunctions.replaceForTable);
    textBox.innerHTML = newText;
    const container = document.querySelector(`[data-fm_table="${id}"]`);
    if (container && tableData !== null && tableData !== void 0 && tableData.length) {
      const tableBody = buildInfoBox({
        container,
        isTable: true,
        id
      });
      const table = createTableElement(tableData);
      if (table) tableBody.appendChild(table);
    }
  }, [replacementFunctions, buildInfoBox, createTableElement]);

  /**
   * Main function to insert text content into DOM with live processing
   * @param {Object} params - Insertion parameters
   * @param {string} [params.text] - Text content to insert
   * @param {string} [params.progress] - Progress text to display
   * @param {HTMLElement} params.chatBox - Chat container element
   * @param {string} [params.prevTextRef] - Previous text reference for diff calculation
   * @param {Object} [params.reports] - Chart data object
   * @param {Object} [params.tables] - Table data object
   */
  const insertTextInDOM = useCallback(_ref2 => {
    let {
      text,
      progress,
      chatBox,
      prevTextRef,
      reports,
      tables
    } = _ref2;
    if (!chatBox || !text && !progress) return;
    const textBox = chatBox.querySelector('.dana-chat-comment-text__inner');
    const prevText = prevTextRef || '';
    if (progress) {
      textBox.classList.toggle('dana-chat-comment-text__progress', true);
      textBox.innerHTML = sanitizeHtml(progress);
    } else if (text) {
      textBox.classList.toggle('dana-chat-comment-text__progress', false);
      if (!prevText) textBox.innerHTML = '';
      let textPortion = getDifference(prevText, text);
      textPortion = processLiveMarkdown(textPortion);
      textBox.insertAdjacentHTML('beforeend', sanitizeHtml(textPortion));
      processLiveRedirect(textBox);
      processLiveTable(textBox, tables);
      processLiveChart(textBox, reports);
    }
  }, [processLiveRedirect, processLiveTable, processLiveChart]);

  /**
   * Generates a markdown table from data array
   * @param {Array} data - Array of objects representing table rows
   * @returns {string} Formatted markdown table string
   */
  const generateMarkdownTable = useCallback(data => {
    if (!(data !== null && data !== void 0 && data.length)) return '';
    const headers = Object.keys(data[0]);
    const columnWidths = headers.reduce((acc, header) => {
      acc[header] = Math.max(header.length, ...data.map(row => {
        var _row$header;
        return ((_row$header = row[header]) === null || _row$header === void 0 ? void 0 : _row$header.toString().length) || 0;
      }));
      return acc;
    }, {});
    let markdownTable = '|';
    headers.forEach(header => {
      markdownTable += ` ${header.padEnd(columnWidths[header])} |`;
    });
    markdownTable += '\n|';
    headers.forEach(header => {
      markdownTable += ` ${'-'.repeat(columnWidths[header])} |`;
    });
    markdownTable += '\n';
    data.forEach(row => {
      markdownTable += '|';
      headers.forEach(header => {
        let cleanValue = '';
        const value = row[header];
        if (value && typeof value === 'string') {
          cleanValue = value.replace(DETAIL_REGEX_GLOBAL, text => {
            const info = getRegexMatch(text, DETAIL_REGEX);
            const [,, name] = info.split(',');
            return name || '';
          });
        } else {
          cleanValue = value || '';
        }
        markdownTable += ` ${cleanValue.toString().padEnd(columnWidths[header])} |`;
      });
      markdownTable += '\n';
    });
    return `\n${markdownTable}`;
  }, []);

  /**
   * Cleans chat text for clipboard copying by removing HTML tags and converting
   * special elements (charts, tables) to markdown format
   * @param {Object} params - Cleaning parameters
   * @param {string} params.value - Text content to clean
   * @param {Object} [params.tables] - Table data object
   * @param {Object} [params.reports] - Chart data object
   * @returns {string} Cleaned text suitable for clipboard
   */
  const cleanChatTextForClipboard = useCallback(_ref3 => {
    let {
      value,
      tables,
      reports
    } = _ref3;
    let finalText = value;
    finalText = removeTags(finalText);
    finalText = removeMarkdown(finalText);

    // Replace detail links with plain text
    finalText = finalText.replace(DETAIL_REGEX_GLOBAL, text => {
      const info = getRegexMatch(text, DETAIL_REGEX);
      const [,, name] = info.split(',');
      return name ? `"${name}"` : text;
    });

    // Replace tables with markdown
    if (tables) {
      finalText = finalText.replace(TABLE_REGEX_GLOBAL, text => {
        var _tables$id;
        const {
          info
        } = getRegexExec(text, TABLE_REGEX);
        const id = info;
        if (!id || !((_tables$id = tables[id]) !== null && _tables$id !== void 0 && _tables$id.length)) return '';
        return generateMarkdownTable(tables[id]);
      });
    }

    // Remove charts placeholders
    finalText = finalText.replace(CHART_REGEX_GLOBAL, '');
    return finalText;
  }, [generateMarkdownTable]);
  const parseDetailLinks = useCallback(text => {
    var _getEntityFromString;
    const {
      match,
      info
    } = getRegexExec(text, DETAIL_REGEX);
    if (!match || !info) return text;
    const [entityName, id, name, type] = info.split(',');
    const route = (_getEntityFromString = getEntityFromString(entityName)) === null || _getEntityFromString === void 0 ? void 0 : _getEntityFromString.route;
    const newText = text.replace(match, !!type ? `[${name}](/#${route}/${id}/${type})` : `[${name}](/#${route}/${id})`);
    return newText;
  }, []);
  const parseChartEmbeds = useCallback(text => {
    const id = getRegexMatch(text, CHART_REGEX);
    return id ? `[](#fm_report '{"id":"${id}"}')` : text;
  }, []);
  const parseTableEmbeds = useCallback(text => {
    const id = getRegexMatch(text, TABLE_REGEX);
    return id ? `[](#fm_table '{"id":"${id}"}')` : text;
  }, []);
  const parseCopilotEmbeds = useCallback(text => {
    if (!text) return;
    let newText = text.replace(DETAIL_REGEX_GLOBAL, parseDetailLinks);
    newText = newText.replace(CHART_REGEX_GLOBAL, parseChartEmbeds);
    newText = newText.replace(TABLE_REGEX_GLOBAL, parseTableEmbeds);
    return newText;
  }, [parseChartEmbeds, parseDetailLinks, parseTableEmbeds]);
  return {
    insertTextInDOM,
    cleanChatTextForClipboard,
    parseCopilotEmbeds
  };
};
export default useBuildAnswer;
export { COPILOT_CHAT_STREAM_IDENTIFIERS };